import axios, { AxiosProgressEvent, AxiosResponse, HttpStatusCode } from 'axios';
import $api, { API_URL } from '@/http/index';
import { sendNotification } from '@srm/ui/lib/notifications';
import { INotification } from '@srm/ui/notifications';
import { auth } from '@/auth';
import { triggerBlobDownload } from '@srm/ui/lib/download';
import { errorTraceId } from '@srm/core/errors';

/** Опции скачивания: отмена (signal) и прогресс (onProgress). */
export interface DownloadOptions {
    signal?: AbortSignal;
    onProgress?: (event: AxiosProgressEvent) => void;
    /**
     * Показывать тост при ошибке скачивания (по умолчанию true).
     * Передайте false, если вызывающий код сам выводит ошибку (например, в модалке),
     * чтобы не показывать её дважды.
     */
    notifyOnError?: boolean;
    /**
     * Тост на 404 вместо общего «Не удалось скачать файл»: файл, который сервер уже убрал (вышел срок хранения),
     * — не сбой, а ожидаемое состояние, и пользователю нужно сказать, почему его нет.
     */
    notFoundNotification?: INotification;
}

export class FilesService {
    static downloadFileNative(url: string): void {
        this.clickLink(this.getDirectDownloadUrl(url));
    }

    /**
     * Скачать браузером по прямой ссылке с токеном страницы: файл пишется на диск потоком, ход — в загрузках браузера,
     * в память вкладки он не ложится. Ссылка — `download`: страница не уходит и не спрашивает о несохранённом.
     * Без токена (страница прежнего пользователя) ничего не начато — `false`.
     */
    static tryDownloadFileNative(url: string): boolean {
        if (!auth.pageAccessToken()) return false;
        this.clickLink(this.getDirectDownloadUrl(url), true);
        return true;
    }

    private static clickLink(href: string, download = false): void {
        const link = document.createElement('a');
        link.href = href;
        if (download) link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    static async downloadFile(
        url: string,
        noContentNotificationOptions?: INotification,
        options?: DownloadOptions,
    ): Promise<void> {
        try {
            const file = await $api.get(`/${url}`, {
                responseType: 'blob',
                suppressErrorNotification: true,
                passThroughStatuses: options?.notFoundNotification ? [HttpStatusCode.NotFound] : undefined,
                signal: options?.signal,
                onDownloadProgress: options?.onProgress,
            });
            this.checkAndDownload(file, noContentNotificationOptions);
        } catch (error) {
            if (
                options?.notFoundNotification &&
                axios.isAxiosError(error) &&
                error.response?.status === HttpStatusCode.NotFound
            ) {
                if (options.notifyOnError !== false) sendNotification(options.notFoundNotification);
            } else if (options?.notifyOnError !== false) this.notifyDownloadError(error);
            throw error;
        }
    }

    static async downloadFilePost(
        url: string,
        data: any,
        config?: any,
        noContentNotificationOptions?: INotification,
        options?: DownloadOptions,
    ): Promise<void> {
        config = {
            ...config,
            responseType: 'blob',
            suppressErrorNotification: true,
            signal: options?.signal ?? config?.signal,
            onDownloadProgress: options?.onProgress ?? config?.onDownloadProgress,
        };
        try {
            const file = await $api.post(`/${url}`, data, config);
            this.checkAndDownload(file, noContentNotificationOptions);
        } catch (error) {
            if (options?.notifyOnError !== false) this.notifyDownloadError(error);
            throw error;
        }
    }

    private static checkAndDownload(file: AxiosResponse<any>, noContentNotificationOptions?: INotification) {
        if (file.status === HttpStatusCode.NoContent) {
            sendNotification(noContentNotificationOptions ?? { message: 'Файл не найден.', variant: 'red' });
            return;
        }
        this.download(file);
    }

    private static download(file: AxiosResponse<any>) {
        const mime = (file.headers['Content-Type'] ?? file.headers['content-type'])?.toString() ?? '';
        const blob = new Blob([file.data], { type: mime });
        const filename = this.getFileName(file.headers['content-disposition']) || 'download';
        triggerBlobDownload(blob, filename);
    }

    /** Показывает пользователю реальную причину неудачного скачивания (а не молчит) и код обращения. */
    private static notifyDownloadError(error: unknown): void {
        if (axios.isCancel(error)) return;
        // Пустое тело ошибки (например, пустой Blob на 404/5xx) даёт ['' ] или '' —
        // отфильтровываем пустышки, чтобы не показать пустой тост вместо фолбэка.
        const message = Array.isArray(error)
            ? error.filter((m): m is string => typeof m === 'string' && m.trim() !== '').join('\n')
            : typeof error === 'string'
              ? error
              : '';
        sendNotification({
            title: 'Не удалось скачать файл',
            message: message.trim() || undefined,
            variant: 'red',
            traceId: errorTraceId(error),
        });
    }

    private static getDirectDownloadUrl(url: string): string {
        const token = auth.pageAccessToken();
        const base = API_URL.startsWith('http')
            ? API_URL
            : `${window.location.origin}/${API_URL.replace(/^\//, '')}`;
        const directUrl = new URL(url.replace(/^\//, ''), base.endsWith('/') ? base : `${base}/`);

        if (token) {
            directUrl.searchParams.set('access_token', token);
        }

        return directUrl.toString();
    }

    private static getFileName(disposition?: string): string {
        if (!disposition) return '';

        const utf8FilenameRegex = /filename([*]?)=UTF-8''([^\";]+)/i;
        const asciiFilenameRegex = /^filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i;

        let fileName: string = '';
        if (utf8FilenameRegex.test(disposition)) {
            let match = utf8FilenameRegex.exec(disposition);
            if (match) {
                fileName = decodeURIComponent(match[2]);
            }
        } else {
            const filenameStart = disposition.toLowerCase().indexOf('filename=');
            if (filenameStart >= 0) {
                const partialDisposition = disposition.slice(filenameStart);
                const matches = asciiFilenameRegex.exec(partialDisposition);
                if (matches != null && matches[2]) {
                    fileName = matches[2];
                }
            }
        }
        return fileName;
    }
}
